import { isMrnPrimary, normalizeIdentifiers } from "@/configuration/mrn-primary";
import { getGroupConfigSubdomain } from "@/configuration/paths";
import { MissingMrnError } from "@/src/lib/error";
import { logAndThrow } from "@/src/lib/logging/logger";
import { stats } from "@/src/lib/logging/stats.server";
import { hasValue } from "@/src/lib/utils/object-utils";
import type { Group } from "@/src/models/group";
import type { Patient } from "@/src/models/patient";
import type { Practitioner } from "@/src/models/practitioner";

type EhrIdKind = "ehrId" | "mrn" | "unknown";

export function classifyEhrId(value: string | null): EhrIdKind {
  if (!value?.trim()) return "unknown";
  // A numeric string of 12 or fewer digits is an MRN. NextGen (InSight) MRNs
  // are 12 digits; ECW MRNs are shorter. Known EHR IDs (NextGen person UUIDs,
  // ECW FHIR base64) always contain non-digit characters.
  if (/^\d{1,12}$/.test(value)) return "mrn";

  return "ehrId";
}

export type EhrIdentifiers = { mrn: string | null; ehrSystemId: string | null };

export type EhrIdSlots = Pick<Patient, "ehrId" | "secondaryEhrId"> | Pick<Practitioner, "ehrId" | "secondaryEhrId">;

// With `group`, trust the column convention literally (see `mrn-primary.ts`).
// Without it, fall back to structural classification — needed for ECW rows
// scrambled across columns, but misclassifies ModMed PMS account IDs like
// "116326PAT000027835" as EHR IDs. Pass `group` whenever it's in scope.
export function resolveEhrIdentifiers(slots: EhrIdSlots, group?: Group | null): EhrIdentifiers {
  const ehrIdCol = slots.ehrId?.trim() || null;
  const secondaryCol = slots.secondaryEhrId?.trim() || null;

  if (group) {
    if (isMrnPrimary(group)) {
      const swapped = classifyEhrId(ehrIdCol) === "ehrId" && classifyEhrId(secondaryCol) === "mrn";

      if (swapped) {
        stats.counter("ehr.patient.swapped_ehr_id_columns", 1, {
          tags: { subdomain: getGroupConfigSubdomain(group) },
        });

        return { ehrSystemId: ehrIdCol, mrn: secondaryCol };
      }

      return { ehrSystemId: secondaryCol, mrn: ehrIdCol };
    }

    return { ehrSystemId: ehrIdCol, mrn: secondaryCol };
  }

  const uniqueCandidates = Array.from(new Set([ehrIdCol, secondaryCol].filter((c): c is string => Boolean(c))));

  if (uniqueCandidates.length === 0) return { ehrSystemId: null, mrn: null };

  // If there's no ambiguity (only one unique value), it's the primary ID.
  // For single-ID EHRs (Athena, Mod-Med), this numeric string is the "EHR ID".
  if (uniqueCandidates.length === 1) {
    const val = uniqueCandidates[0];
    const isMrn = classifyEhrId(val) === "mrn";

    return { ehrSystemId: val, mrn: isMrn ? val : null };
  }

  // Contention: two different values. Use structural classification to assign slots.
  const ehrSystemId = uniqueCandidates.find((c) => classifyEhrId(c) === "ehrId") ?? null;
  const mrn = uniqueCandidates.find((c) => classifyEhrId(c) === "mrn") ?? null;

  return { ehrSystemId, mrn };
}

export function tryGetEhrSystemId(slots: EhrIdSlots, group?: Group | null): string | null {
  return resolveEhrIdentifiers(slots, group).ehrSystemId;
}

export function tryGetMrn(slots: EhrIdSlots, group?: Group | null): string | null {
  return resolveEhrIdentifiers(slots, group).mrn;
}

// Ids from a patient just fetched from the EHR: ehrId is the system id, secondaryEhrId the MRN (no per-group column swap, unlike stored rows).
export function ehrRecordIdentifiers(record: Pick<Patient, "ehrId" | "secondaryEhrId">): EhrIdentifiers {
  return { ehrSystemId: record.ehrId?.trim() || null, mrn: record.secondaryEhrId?.trim() || null };
}

export function requireEhrSystemId(patient: Patient, group?: Group | null): string {
  const ehrId = tryGetEhrSystemId(patient, group);

  return hasValue(ehrId) && ehrId ? ehrId : logAndThrow("Patient EHR ID not found", { patientId: patient.id });
}

/**
 * Returns the medical record number from `ehrId` / `secondaryEhrId`, using the group's column
 * convention when `group` is passed (see `resolveEhrIdentifiers`). Throws if no MRN is present.
 *
 * - **Patient** — MRN sent to the EHR for scheduling, document upload, and patient lookup
 *   (e.g. Cobalt `patient_mrn`, CloudCruise workflows). Prefer passing `group` so MRN vs EHR ID
 *   is not guessed from digit shape alone.
 * - **Practitioner** — provider identifier stored in the same two columns (e.g. Cobalt scheduling
 *   requires an MRN on both patient and practitioner before availability/booking calls).
 */
export function requireMrn(patient: Patient, group?: Group | null): string;
export function requireMrn(practitioner: Practitioner, group?: Group | null): string;
export function requireMrn(entity: Patient | Practitioner, group?: Group | null): string {
  const mrn = tryGetMrn(entity, group);

  if (hasValue(mrn) && mrn) {
    return mrn;
  }

  // Typed error (not logAndThrow's bare Error) so agent endpoints can surface 422/missing_data
  // instead of 500/http_5xx. The boundary handler logs it; the id rides on meta. See VAL-2006.
  if ("role" in entity) {
    throw new MissingMrnError("Practitioner MRN not found", { practitionerId: entity.id });
  }

  throw new MissingMrnError("Patient MRN not found", { patientId: entity.id });
}

export function tryAnyEhrId(patient: Patient): string | null {
  return patient.ehrId?.trim() || patient.secondaryEhrId?.trim() || null;
}

export function requireAnyEhrId(patient: Patient): string {
  const ehrId = tryAnyEhrId(patient);

  return hasValue(ehrId) && ehrId ? ehrId : logAndThrow("Patient EHR ID not found", { patientId: patient.id });
}

// A "MRN" equal to the EHR system id is the eCW double-FHIR-id corruption (the FHIR resource id in
// the MRN slot), never a real MRN. Applied to both the incoming record and the stored row: because
// `tryGetMrn` trusts column position for MRN-primary groups, an already-corrupted stored row would
// otherwise report its FHIR id as the MRN — tripping a false conflict and blocking repair.
function realMrnOrNull(mrn: string | null, ehrSystemId: string | null): string | null {
  return mrn && mrn !== ehrSystemId ? mrn : null;
}

export type MergedEhrIdentifiers =
  | {
      conflict: true;
      incomingEhrId: string | null;
      existingEhrId: string | null;
      incomingMrn: string | null;
      existingMrn: string | null;
    }
  | { conflict: false; ehrId: string | null; secondaryEhrId: string | null };

/**
 * Merges EHR identifiers when updating an already-identified patient, honoring the group's column
 * convention. `incoming` is read as EHR-record layout (ehrId = system id, secondaryEhrId = MRN) — how
 * a record arrives from an EHR/FHIR patient search; `existing` is a stored row that follows the
 * group's column convention. Returns the columns to write, or a `conflict` signal (caller should skip
 * the update, leaving the existing row untouched) when incoming identifiers disagree with existing
 * non-null ones.
 *
 * Both sides drop a "MRN" equal to their EHR system id — the eCW double-FHIR-id corruption (the FHIR
 * resource id sitting in the MRN slot), never a real MRN. Guarding the stored row matters too:
 * `tryGetMrn` trusts column position for MRN-primary groups, so an already-corrupted row would report
 * its FHIR id as the MRN, tripping a false conflict and blocking a healthy incoming record from
 * repairing it.
 *
 * An incoming record with no system id (e.g. Cobalt, which exposes an MRN and no FHIR id) contributes
 * only its MRN; the stored EHR link is carried through untouched.
 */
export function mergeEhrIdentifiers(
  incoming: Pick<Patient, "ehrId" | "secondaryEhrId">,
  existing: Pick<Patient, "ehrId" | "secondaryEhrId">,
  group: Group
): MergedEhrIdentifiers {
  const incomingIds = ehrRecordIdentifiers(incoming);
  const incomingEhrId = incomingIds.ehrSystemId;
  const incomingMrn = realMrnOrNull(incomingIds.mrn, incomingEhrId);
  const existingEhrId = tryGetEhrSystemId(existing, group);
  const existingMrn = realMrnOrNull(tryGetMrn(existing, group), existingEhrId);

  const ehrIdConflict = Boolean(incomingEhrId && existingEhrId && incomingEhrId !== existingEhrId);
  const mrnConflict = Boolean(incomingMrn && existingMrn && incomingMrn !== existingMrn);

  if (ehrIdConflict || mrnConflict) {
    return { conflict: true, incomingEhrId, existingEhrId, incomingMrn, existingMrn };
  }

  return {
    conflict: false,
    ...normalizeIdentifiers(group, incomingEhrId ?? existingEhrId, incomingMrn ?? existingMrn),
  };
}
